import React from 'react';
import ReactDOM from 'react-dom';
import OlOverlay from 'ol/Overlay';
import { fromEPSG4326 } from 'ol/proj/epsg3857';
import { className, fromProps } from '../../../lib/className';
import { capitalize } from '../../../misc/misc';
import { BadgeTypes } from '../../custom/widgets/RunwayInfo';

class OwMapMarker {
	/**
	 * @param {Object} props
	 * @param {DOMElement} props.box
	 * @param {Array.<number>} props.coordinates
	 * @param {string} props.id
	 */

	constructor(box, coordinates, id) {
		this.overlay = new OlOverlay({
			element: box,
			position: fromEPSG4326(coordinates),
			positioning: 'top-left',
			id: id,
			stopEvent: false,
		});
	}

	onClick(callback) {}

	getOverlay() {
		return this.overlay;
	}

	moveTo(coordinates) {
		const position = fromEPSG4326(coordinates);
		const overlayPosition = this.overlay.getPosition();
		if (overlayPosition[0] == position[0] && overlayPosition[1] == position[1]) return;
		this.overlay.setPosition(position);
		this.updatePresence();
	}

	hide(hidden = true) {
		if (hidden == Boolean(this.hidden)) return;
		this.hidden = hidden;
		this.updatePresence();
	}

	addToMap(map) {
		this.map = map;
		this.updatePresence();
		// this.mapListener = () => this.updatePresence();
		// this.map.getOlMap().on('moveend', this.mapListener);
	}

	updatePresence() {
		const present = !this.hidden; // && containsCoordinate(this.map.getBufferedExtent(), this.overlay.getPosition());
		if (present == Boolean(this.present)) return;
		const olMap = this.map.getOlMap();
		if (present) olMap.addOverlay(this.overlay);
		else olMap.removeOverlay(this.overlay);
		this.present = present;
	}

	destroy() {
		if (this.map) {
			const olMap = this.map.getOlMap();
			// olMap.un('moveend', this.mapListener); this.mapListener = null;
			olMap.removeOverlay(this.overlay);
		}
		this.overlay = null;
	}
}

/**
 * @param {Object} props
 * @param {OwMap} props.map
 * @param {Array.<number>} props.coordinates [decimal, decimal] -- (lon, lat)
 * @param {string} props.id
 * @param {string} [props.className]
 * @param {function} [props.onClick]
 * @param {Object} [props.params]
 * @param {Object} [props.properties]
 * @param {Object} [props.bodyStyle]
 * @param {string} [props.title]
 * @param {string} [props.label]
 * @param {React.Component} [props.customElement]
 */
class MapMarker extends React.Component {

	shouldComponentUpdate(nextProps, nextState) {
		const props = this.props;
		if (props.coordinates[0] != nextProps.coordinates[0] || props.coordinates[1] != nextProps.coordinates[1]) return true;
		if (props.className != nextProps.className) return true;
		if (props.bodyStyle != nextProps.bodyStyle) return true;
		if (Boolean(props.params) != Boolean(nextProps.params)) return true;
		if (props.params) {
			const nextPropKeys = Object.keys(nextProps.params);
			if (Object.keys(props.params).length != nextPropKeys.length) return true;
			if (nextPropKeys.some(key => nextProps.params[key] != props.params[key])) return true;
		}
		if (props.label != nextProps.label) return true;
		if (props.title != nextProps.title) return true;
		if (props.id != nextProps.id) return true;
		return false;
	}

	render() {
		if (!this.marker) this.attach();
		else this.update();
		const props = this.props;
		const overlay = this.marker.getOverlay();
		if (props.params) {
			overlay.autoPan = props.params.autoPan;
			overlay.autoPanMargin = props.params.autoPanMargin;
		}
		if (props.properties) overlay.setProperties(props.properties);
		this.marker.moveTo(props.coordinates);
		if (props.bodyStyle) Object.keys(props.bodyStyle).forEach((name) => {
			this.body.style[name] = props.bodyStyle[name];
		});
		if (this.marker && props.customElement) return ReactDOM.createPortal(
			<div className="custom-element-map-marker">{props.customElement}</div>,
			this.marker.getOverlay().getElement().parentNode,
		);
		return null;
	}

	attach() {
		const props = this.props;
		this.box = document.createElement('div');
		this.box.addEventListener('click', () => {
			if (this.props.onClick) this.props.onClick();
		});
		this.box.className = 'marker';
		this.body = this.box.appendChild(document.createElement('div'));
		this.body.className = 'body';
		this.marker = new OwMapMarker(this.box, props.coordinates, props.id);
		this.marker.addToMap(props.map);
		this.marker.getOverlay().set('displayLabel', this.updateLabel.bind(this));
		this.update();
	}

	update() {
		this.box.className = className('marker', fromProps(this.props));
		this.box.title = capitalize(this.props.title);
		this.updateBadge();
		this.updateIssueIcon();
		this.updateLabel();
	}

	updateBadge() {
		const decoration = this.props.params?.badgeType ? this.determineBadgeDecoration(this.props.params.badgeType) : null;
		if (Boolean(this.badgeDecoration) == Boolean(decoration)) {
			if (decoration == null) return;
			if (decoration.badgeClass == this.badgeDecoration && decoration.label == this.badgeDecoration.label) return;
		}
		if (this.badge) {
			this.badge.parentNode.removeChild(this.badge);
			this.badge = null;
		}
		if (this.badgeDecoration?.badgeClass) this.box.classList.remove(this.badgeDecoration.badgeClass);
		if (decoration) {
			if (decoration.badgeClass) this.box.classList.add(decoration.badgeClass);
			if (decoration.label != null) {
				const badge = document.createElement('div');
				badge.classList.add('marker-badge');
				badge.appendChild(document.createElement('span')).innerText = decoration.label;
				this.box.insertBefore(badge, this.body.nextSibling);
				this.badge = badge;
			}
		}
		this.badgeDecoration = decoration;
	}

	determineBadgeDecoration(badgeType) {
		switch (badgeType) {
			case BadgeTypes.InOpenedRunwayRed: return { badgeClass: 'red-badge', label: 'RWY' };
			case BadgeTypes.InClosedRunwayRed: return { badgeClass: 'red-badge', label: 'REP' };
			case BadgeTypes.InTaxiwayRed: return { badgeClass: 'red-badge' };
			case BadgeTypes.InOpenedRunwayGreen: return { badgeClass: 'green-badge', label: 'RWY' };
			case BadgeTypes.InClosedRunwayGreen: return { badgeClass: 'green-badge', label: 'REP' };
			case BadgeTypes.NearOpenedRunwayGreen:
			case BadgeTypes.InTaxiwayGreen: return { badgeClass: 'green-badge' };
			case BadgeTypes.InOpenedRunwayOrange: return { badgeClass: 'orange-badge', label: 'RWY' };
			case BadgeTypes.InClosedRunwayOrange: return { badgeClass: 'orange-badge', label: 'REP' };
			case BadgeTypes.NearOpenedRunwayOrange: return { badgeClass: 'orange-badge', label: '' };
			case BadgeTypes.WarningGreen: return { badgeClass: 'warning-green', label: '' };
			case BadgeTypes.AlertGreen: return { badgeClass: 'alert-green', label: '' };
			case BadgeTypes.AlertOrange: return { badgeClass: 'alert-orange', label: '' };
			case BadgeTypes.SevereGreen: return { badgeClass: 'severe-green', label: '' };
			case BadgeTypes.SevereRed: return { badgeClass: 'severe-red', label: '' };
			case BadgeTypes.WarningGreenRWY: return { badgeClass: 'warning-green-rwy', label: 'RWY' };
			case BadgeTypes.AlertGreenRWY: return { badgeClass: 'alert-green-rwy', label: 'RWY' };
			case BadgeTypes.AlertOrangeRWY: return { badgeClass: 'alert-orange-rwy', label: 'RWY' };
			case BadgeTypes.SevereGreenRWY: return { badgeClass: 'severe-green-rwy', label: 'RWY' };
			case BadgeTypes.SevereRedRWY: return { badgeClass: 'severe-red-rwy', label: 'RWY' };
		}
		return null;
	}

	updateIssueIcon() {
		const required = Boolean(this.props.params?.isAlerting);
		if (required == Boolean(this.icon)) return;
		if (!required) {
			this.icon.parentNode.removeChild(this.icon);
			this.icon = null;
			return;
		}
		const icon = document.createElement('span');
		icon.classList.add('unresolved-issue-icon');
		this.box.insertBefore(icon, this.label);
		this.icon = icon;
	}

	updateLabel() {
		const text = this.props.map.isDisplayedMarkerLabels() && this.props.label;
		if (!text) {
			if (this.label) {
				this.label.parentNode.removeChild(this.label);
				this.label = null;
			}
			return;
		}
		if (this.label) {
			this.label.firstChild.innerText = text;
			return;
		}
		const label = document.createElement('div');
		label.classList.add('marker-label');
		label.appendChild(document.createElement('div')).innerText = text;
		this.box.appendChild(label);
		this.label = label;
	}

	setZIndex = (zIndex) => {
		if (this.marker) this.marker.getOverlay().getElement().parentNode.style.zIndex = zIndex;
		else console.log('setZIndex, no marker', this);
	};

	getDomBox() {
		return this.marker.getOverlay().getElement();
	}

	componentWillUnmount() {
		if (this.marker) {
			this.marker.destroy();
			this.marker = null;
		}
	}
}

export default MapMarker;
